import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { societyInfo } from '../data/mock';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [memberAccessUrl, setMemberAccessUrl] = useState('#');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Fetch member access URL from settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data?.member_access_url) {
          setMemberAccessUrl(response.data.member_access_url);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const navLinks = [
    { href: '#about', label: t.nav.about },
    { href: '#history', label: t.nav.history },
    { href: '#area', label: t.nav.area },
    { href: '#lodges', label: t.nav.lodges },
    { href: '#news', label: t.nav.news },
    { href: '#contact', label: t.nav.contact },
  ];

  const damageReportUrl = 'https://egozd.zgs.si/#/egozd/PrijavaSkode';
  const damageReportLabel = language === 'sl' ? 'Prijava škode' : 'Damage Report';
  const campLabel = language === 'sl' ? 'Lovski tabor' : 'Hunting Camp';

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Anchor not on current page — go to homepage with hash
      window.location.assign(`/${href}`);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-stone-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-3 group"
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden bg-white shadow-lg group-hover:shadow-emerald-500/25 transition-shadow duration-300">
              <img 
                src={societyInfo.logo} 
                alt="LD Rakek Logo" 
                className="w-12 h-12 object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <span className="block text-lg font-bold text-white tracking-wide">
                LD Rakek
              </span>
              <span className="block text-xs text-emerald-500 tracking-wider uppercase font-semibold">
                {language === 'sl' ? 'Od leta 1946' : 'Since 1946'}
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="px-4 py-2 text-sm font-medium text-stone-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              >
                {link.label}
              </button>
            ))}
            <a
              href="/lovski-tabor"
              className="px-4 py-2 text-sm font-medium text-stone-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              data-testid="camp-nav-btn"
            >
              {campLabel}
            </a>
            <a
              href={damageReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-stone-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              data-testid="damage-report-btn"
            >
              {damageReportLabel}
            </a>
            <a
              href={memberAccessUrl}
              target={memberAccessUrl !== '#' ? '_blank' : undefined}
              rel={memberAccessUrl !== '#' ? 'noopener noreferrer' : undefined}
              className="ml-2 px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-all duration-200"
              data-testid="member-access-btn"
            >
              {language === 'sl' ? 'Dostop za člane' : 'Member Access'}
            </a>
          </nav>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/20 hover:text-emerald-300 hover:border-emerald-500 transition-all duration-200"
            >
              {language === 'sl' ? 'EN' : 'SL'}
            </Button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-stone-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-emerald-600/30 bg-emerald-900 rounded-b-xl mx-2 mb-2 px-2">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="px-4 py-3 text-left text-white font-medium hover:bg-emerald-800 rounded-lg transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="/lovski-tabor"
                className="px-4 py-3 text-left text-white font-medium hover:bg-emerald-800 rounded-lg transition-colors"
                data-testid="camp-nav-btn-mobile"
              >
                {campLabel}
              </a>
              <a
                href={damageReportUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-left text-white font-medium hover:bg-emerald-800 rounded-lg transition-colors"
                data-testid="damage-report-btn-mobile"
              >
                {damageReportLabel}
              </a>
              <a
                href={memberAccessUrl}
                target={memberAccessUrl !== '#' ? '_blank' : undefined}
                rel={memberAccessUrl !== '#' ? 'noopener noreferrer' : undefined}
                className="mx-2 mt-2 px-4 py-3 text-center font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-all duration-200"
                data-testid="member-access-btn-mobile"
              >
                {language === 'sl' ? 'Dostop za člane' : 'Member Access'}
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
